/* eslint-disable react/jsx-props-no-spreading */
/**
 * @file
 *
 * Verify by MDVIP ID and DOB form.
 *
 * example:
 * <IDForm/>
 */
import { zodResolver } from '@hookform/resolvers/zod'
import { createRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha-enterprise'
import { Controller, useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { z } from 'zod'

import { RECAPTCHA_CLIENT_KEY } from '../../config/config'
import { MDVIP_VERIFY_ACCOUNT_DEFAULT_ERROR_HTML, MDVIP_VERIFY_ACCOUNT_ERROR_MESSAGE } from '../../Constants'
import useAuthenticateContext from '../../hooks/useAuthenticate'
import { initLogRocket } from '../../utils/logrocket'
import { dobPattern } from '../../utils/patterns'
import { ContactMDVIPMessage } from '../ContactMDVIPMessage'
import { toast } from '../CustomToastMessage/toast'
import Input from '../form-elements/Input'
import InputDate from '../form-elements/Input/InputDate'
import LoadingSpinner from '../misc/Spinners/LoadingSpinner'
import FormContinueButton from './FormContinueButton'

const idFormSchema = z.object({
  mdvipId: z
    .string()
    .nonempty({ message: 'Please enter a valid MDVIP ID.' })
    .min(5, { message: 'Please enter a valid MDVIP ID.' }),
  dob: z
    .string()
    .nonempty({ message: 'Please enter a valid Date of Birth.' })
    .refine((value) => dobPattern.test(value), {
      message: 'Please enter a valid Date of Birth.',
    }),
})

interface IDFormProps {
  mdvipId: string
  dob: string
}

const IDForm = () => {
  const { loading, setParams } = useAuthenticateContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const expired = searchParams.get('expired') === 'true'

  const {
    handleSubmit,
    control,
    formState: { isValid },
    reset,
  } = useForm<IDFormProps>({
    mode: 'onTouched',
    resolver: zodResolver(idFormSchema),
    defaultValues: { mdvipId: '', dob: '' },
  })

  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)

  const [message, setMessage] = useState('')
  const captchaRef = createRef<typeof ReCAPTCHA>()

  const isFormComplete = isValid && (!RECAPTCHA_CLIENT_KEY || recaptchaToken) && !expired && !message

  const resetRecaptcha = () => {
    captchaRef.current?.reset()
  }

  const resetForm = () => {
    reset()
    resetRecaptcha()
  }

  const onSubmit = async (data: IDFormProps) => {
    const { mdvipId, dob } = data

    await initLogRocket('mdvipId', mdvipId)

    // Send values to API.
    setParams({
      mdvipId,
      dob,
      recaptchaToken,
      onAuthenticateError: () => {
        setMessage(MDVIP_VERIFY_ACCOUNT_DEFAULT_ERROR_HTML)
        toast(<ContactMDVIPMessage initialMessage={MDVIP_VERIFY_ACCOUNT_ERROR_MESSAGE} />)
        resetForm()
      },
    })
    setMessage('')
    resetRecaptcha()
  }

  return (
    <>
      {loading && <LoadingSpinner loading={loading} />}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="mdvipId"
          control={control}
          render={({ field: { onChange, ref, ...rest }, fieldState: { error } }) => (
            <Input
              privateData
              label="MDVIP ID"
              onChange={(e) => {
                setSearchParams({})
                setMessage('')
                if (!/^\d*$/.test(e.target.value)) return
                onChange(e.target.value)
              }}
              maxLength={16}
              requiredMessage={error?.message}
              {...rest}
            />
          )}
        />

        <Controller
          name="dob"
          control={control}
          render={({ field: { onChange, ref, ...rest }, fieldState: { error } }) => (
            <InputDate
              privateData
              label="Date of Birth (MM/DD/YYYY)"
              requiredMessage={error?.message}
              onChange={(e) => {
                setSearchParams({})
                setMessage('')
                onChange(e.target.value)
              }}
              {...rest}
            />
          )}
        />

        {RECAPTCHA_CLIENT_KEY && (
          <ReCAPTCHA ref={captchaRef} sitekey={RECAPTCHA_CLIENT_KEY} onChange={setRecaptchaToken} />
        )}
        <FormContinueButton isFormComplete={!!isFormComplete} />
      </form>
    </>
  )
}

export default IDForm
